import { InfoOutlined, ReportProblemOutlined } from '@mui/icons-material'
import { Autocomplete, Box, Button, Checkbox, MenuItem, Paper, Typography, useTheme } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdAdd } from 'react-icons/md'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { useGetApprovedByCustomers } from 'src/api/customer'
import { invalidateMainComponentCache, useChangeMainComponentOwner } from 'src/api/main-component'
import { invalidateMainComponentTypeCache } from 'src/api/main-component-type'
import { invalidateServiceStationCache } from 'src/api/service-station'
import { useGetSuppliersForDropdown } from 'src/api/supplier'
import { AppTextField } from 'src/components/AppTextField'
import { Chip } from 'src/components/common/Chip'
import { AppForm } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import { Spinner } from 'src/components/Spinner'
import { MainComponentStatus } from 'src/enums'
import { ISupplier, ISupplierDropDown } from 'src/generated-types'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { useZodForm } from 'src/hooks/zod-form'
import { MUIThemeColors } from 'src/mui-theme'
import { GenericSupplierFormFields } from 'src/screens/admin/supplier/constants'
import { CreateSupplierDialog } from 'src/screens/customer/suppliers/SupplierContainer'
import { ShowDialogProps } from 'src/show-dialog'
import { Flex } from 'src/styles/flexComponent'
import { Padding } from 'src/styles/paddingComponent'
import { getThemeColor } from 'src/utils'
import { z } from 'zod'

interface ChangeEquipmentOwnerModalProps extends ShowDialogProps {
  customerId: EntityId
  previousOwnerId: EntityId
  equipmentId: EntityId
  parentSupplierId?: EntityId
  status?: MainComponentStatus
  serviceStationId?: EntityId
  serviceStationOwnerId: EntityId
  equipmentTypeId?: EntityId
  currentSupplier?: ISupplier
}
const ChangeEquipmentOwnerModal = ({
  previousOwnerId,
  equipmentId,
  parentSupplierId,
  pop,
  status,
  serviceStationId,
  serviceStationOwnerId,
  equipmentTypeId,
  currentSupplier,
}: ChangeEquipmentOwnerModalProps) => {
  const { t: translate } = useTranslation(['main-component', 'supplier', 'common', 'document-category'])
  const theme = useTheme()
  const [selectedCustomerId, setSelectedCustomerId] = useState<EntityId | null>(null)
  const { data: approvedByCustomers, isLoading } = useGetApprovedByCustomers(previousOwnerId)
  const { mutate: changeOwner, isLoading: isChangingOwner } = useChangeMainComponentOwner(
    serviceStationOwnerId,
    equipmentId,
  )
  const [errors, setErrors] = useState({ customer: false, supplier: false })
  const { data: customerSuppliers, isLoading: isSuppliersLoading } = useGetSuppliersForDropdown(
    parentSupplierId as EntityId,
    selectedCustomerId as EntityId,
  )
  const [supplierOption, setSupplierOption] = useState<ISupplierDropDown | null>(null)
  const [selectedSupplier, setSelectedSupplier] = useState<ISupplierDropDown | null>(null)
  const [isChangingSupplier, setIsChangingSupplier] = useState(false)
  const [isCurrentSupplierNameExisting, setIsCurrentSupplierNameExisting] = useState(false)
  const [isSameSupplierParent, setIsSameSupplierParent] = useState(false)
  const [existingSupplier, setExistingSupplier] = useState<ISupplierDropDown | null>(null)
  const [isSettingSupplier, setIsSettingSupplier] = useState(true)
  const { showDialog, closeAllDialogs } = useDialogCtx()

  const schema = z.object({
    ownerId: z.number(),
    supplierId: z.number().or(z.null()).optional(),
    ...GenericSupplierFormFields,
    parentSupplierId: z.number().or(z.null()).optional(),
    contactPerson: z.string().or(z.null()).optional(),
    previousOwnerId: z.number(),
  })

  const form = useZodForm(schema, {
    defaultValues: {
      previousOwnerId: previousOwnerId,
    },
  })

  const supplierList = useMemo(() => {
    if (supplierOption && !customerSuppliers?.find(supplier => supplier.id === supplierOption.id)) {
      return [...(customerSuppliers || []), supplierOption as ISupplierDropDown]
    }
    return customerSuppliers || []
  }, [supplierOption, customerSuppliers])

  useEffect(() => {
    if (customerSuppliers === undefined) {
      setIsSettingSupplier(true)
      return
    }
    if (isChangingSupplier) {
      setIsSettingSupplier(false)
      return
    }
    setIsSettingSupplier(true)
    const existingSupplier = customerSuppliers?.find(supplier => currentSupplier?.name === supplier.name)
    if (existingSupplier) {
      setIsChangingSupplier(true)
      setIsCurrentSupplierNameExisting(true)
      setSupplierOption(existingSupplier)
      const supplierId = existingSupplier.id === 0 ? null : existingSupplier.id
      setErrors(prev => ({ ...prev, supplier: !supplierId }))
      setSelectedSupplier(existingSupplier)

      setExistingSupplier(existingSupplier || null)

      if (existingSupplier.recommended) {
        setIsSameSupplierParent(true)
      }
    } else {
      setIsCurrentSupplierNameExisting(false)
    }
    form.setValue('supplierId', existingSupplier ? existingSupplier.id : 0)
    form.setValue('name', currentSupplier?.name ?? '')
    form.setValue('parentSupplierId', currentSupplier?.parentSupplierId ?? null)
    form.setValue('contactPerson', currentSupplier?.contactPerson ?? '')
    form.setValue('notes', currentSupplier?.notes ?? '')
    form.setValue('organizationNumber', currentSupplier?.organizationNumber ?? '')
    setIsSettingSupplier(false)
  }, [customerSuppliers, currentSupplier, form, isChangingSupplier])

  const handleChangeOwner = useCallback(
    async form => {
      if (selectedCustomerId) {
        changeOwner(form, {
          onSuccess: () => {
            invalidationActivityLogCache.getMainComponentActivityLogs({
              mainComponentId: equipmentId,
            })
            invalidateMainComponentCache.getMainComponentById(previousOwnerId, equipmentId ?? 0)
            invalidateMainComponentCache.getMainComponentById(selectedCustomerId, equipmentId ?? 0)
            invalidateServiceStationCache.getComponentsByStatus(
              serviceStationOwnerId as EntityId,
              serviceStationId as EntityId,
              status as MainComponentStatus,
            )
            invalidateMainComponentCache.getMainComponentsByComponentType(
              selectedCustomerId as EntityId,
              equipmentTypeId as EntityId,
            )
            invalidateMainComponentCache.getMainComponentsByComponentType(
              previousOwnerId as EntityId,
              equipmentTypeId as EntityId,
            )
            invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(previousOwnerId as EntityId)
            invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(selectedCustomerId as EntityId)
            invalidateMainComponentTypeCache.useGetMainComponentTypeWithMainComponentCount(previousOwnerId)
            invalidateMainComponentTypeCache.useGetMainComponentTypeWithMainComponentCount(selectedCustomerId)
            invalidateServiceStationCache.getCustomerWithComponentCountById(
              serviceStationOwnerId as EntityId,
              serviceStationId as EntityId,
            )
            toast.success(translate('ownerChangedSuccessfully', { ns: 'main-component' }))
            closeAllDialogs()
          },
        })
      }
    },
    [
      selectedCustomerId,
      changeOwner,
      pop,
      serviceStationId,
      serviceStationOwnerId,
      status,
      equipmentId,
      previousOwnerId,
      translate,
    ],
  )

  const onSupplierAdd = useCallback(
    supplier => {
      if (
        customerSuppliers &&
        customerSuppliers?.find(s => s.name.toLocaleLowerCase().trim() === supplier.name.toLocaleLowerCase().trim())
      ) {
        toast.error(translate('supplierNameExists', { ns: 'supplier' }))
        return
      }
      form.setValue('supplierId', 0)
      form.setValue('parentSupplierId', supplier.parentSupplierId)
      form.setValue('contactPerson', supplier.contactPerson ?? '')
      form.setValue('name', supplier.name)
      form.setValue('notes', supplier.notes ?? '')
      form.setValue('organizationNumber', supplier.organizationNumber ?? '')
      setSupplierOption({ ...supplier, id: 0 })
      setSelectedSupplier(supplier)
      setErrors({ ...errors, supplier: false })
    },
    [form, errors, customerSuppliers, translate],
  )

  const onAddSupplier = useCallback(async () => {
    const added = await showDialog(
      CreateSupplierDialog,
      {
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        componentProps: {
          customerId: selectedCustomerId as EntityId,
          parentSupplierId: parentSupplierId,
          onSupplierAdd: onSupplierAdd,
        },
      },
      undefined,
      true,
    )
    if (!added) return
  }, [parentSupplierId, showDialog, selectedCustomerId, onSupplierAdd, translate])

  const onClickCheckbox = useCallback((value: boolean) => {
    setIsChangingSupplier(value)
    setErrors({ ...errors, supplier: false })
  }, [])

  const onSelectCustomer = value => {
    setSelectedCustomerId(value?.id as EntityId)
    setErrors({ ...errors, customer: false })
    form.setValue('ownerId', value?.id as number)
    form.setValue('supplierId', null)
    form.setValue('parentSupplierId', null)
    form.setValue('contactPerson', '')
    form.setValue('notes', '')
    form.setValue('organizationNumber', '')
    setSupplierOption(null)
    setSelectedSupplier(null)
    setIsChangingSupplier(false)
    setIsSameSupplierParent(false)
    setIsCurrentSupplierNameExisting(false)
  }

  const onSelectSupplier = value => {
    setErrors({ ...errors, supplier: false })
    form.setValue('supplierId', value?.id as number)
    form.setValue('name', value?.name ?? '')
    form.setValue('parentSupplierId', value?.parentSupplierId ?? null)
    form.setValue('contactPerson', value?.contactPerson ?? '')
    form.setValue('notes', value?.notes ?? '')
    form.setValue('organizationNumber', value?.organizationNumber ?? '')
    setSelectedSupplier({ ...value, id: value?.id ?? 0 } as ISupplierDropDown)
  }

  return (
    <GenericDialogLayout
      title={translate('changeOwner', { ns: 'main-component' })}
      pop={pop}
      actions={[
        <MainButton
          key="submit"
          onClick={() => {
            const errors = {
              customer: !selectedCustomerId,
              supplier: form.getValues('supplierId') === null || form.getValues('supplierId') === undefined,
            }

            if (errors.customer || errors.supplier) {
              setErrors(errors)
              return
            }

            form.handleSubmit(handleChangeOwner)()
          }}
        >
          {isChangingOwner && <Spinner size={20} />}
          {translate('changeOwner', { ns: 'main-component' })}
        </MainButton>,
        <MainButton key="cancel" onClick={pop} variant="outlined">
          {translate('cancel', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      <Box>
        <AppForm form={form} onSubmit={handleChangeOwner}>
          <Autocomplete
            options={approvedByCustomers || []}
            onChange={(_, value) => onSelectCustomer(value)}
            getOptionLabel={o => o.name}
            loading={isLoading}
            renderOption={(props, o) => (
              <MenuItem {...props} key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            )}
            renderInput={params => (
              <AppTextField
                {...params}
                label={translate('customers', { ns: 'users' })}
                inputProps={{
                  ...params.inputProps,
                }}
                placeholder={translate('customer-select', { ns: 'main-component' })}
              />
            )}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
          />
          {errors.customer && (
            <Typography color="error" variant="body2" sx={{ paddingLeft: '8px', paddingBottom: '12px' }}>
              {translate('select-valid-customer', { ns: 'main-component' })}
            </Typography>
          )}
          {selectedCustomerId && !isSettingSupplier && (
            <Box display={'flex'} flexDirection={'column'} gap={1.5}>
              {!isChangingSupplier && !isCurrentSupplierNameExisting && (
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  alignItems={'center'}
                  gap={1}
                  paddingLeft={1}
                  paddingRight={1}
                >
                  <InfoOutlined sx={{ fontSize: '16px', flexShrink: 0, color: 'error.main' }} />
                  <Typography variant="body2" sx={{ textAlign: 'justify' }} fontWeight={400}>
                    {translate('use-existing-supplier-message', { ns: 'main-component' })}
                  </Typography>
                </Box>
              )}
              {isCurrentSupplierNameExisting && selectedSupplier?.id === existingSupplier?.id && (
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  alignItems={'center'}
                  gap={1}
                  paddingLeft={'12px'}
                  paddingRight={1}
                >
                  {isSameSupplierParent ? (
                    <InfoOutlined sx={{ fontSize: '16px', flexShrink: 0, color: 'error.main' }} />
                  ) : (
                    <ReportProblemOutlined sx={{ fontSize: '16px', flexShrink: 0, color: 'error.main' }} />
                  )}
                  <Typography variant="body2" sx={{ textAlign: 'justify', fontWeight: 400 }}>
                    {translate(
                      isSameSupplierParent
                        ? 'use-supplier-name-existing-message'
                        : 'use-supplier-name-existing-with-different-parent-message',
                      { ns: 'main-component' },
                    )}
                  </Typography>
                </Box>
              )}
              <Flex.Row className="pr-16-core">
                <Checkbox
                  checked={isChangingSupplier}
                  onClick={() => onClickCheckbox(!isChangingSupplier)}
                  disabled={isCurrentSupplierNameExisting}
                />
                <Padding.pt10 className="pr-8-core">
                  <Typography
                    variant="body1"
                    sx={{ textAlign: 'justify', color: getThemeColor(theme, MUIThemeColors.primaryMain) }}
                  >
                    {translate('supplier-change-label', { ns: 'main-component' })}
                  </Typography>
                </Padding.pt10>
              </Flex.Row>
            </Box>
          )}
          {isChangingSupplier && (
            <Autocomplete
              options={supplierList || []}
              onChange={(_, value) => onSelectSupplier(value)}
              getOptionLabel={o => o.name}
              value={selectedSupplier}
              loading={isLoading}
              renderOption={(props, o) => (
                <MenuItem {...props} key={o.id} value={o.id} selected={o.id === selectedSupplier?.id}>
                  {o.name}
                  {o.recommended && (
                    <Chip
                      sx={{
                        ml: 1,
                        height: '20px',
                        fontSize: '0.75rem',
                      }}
                      variant="filled"
                      color="info"
                      label={translate('recommended', { ns: 'common' })}
                    />
                  )}
                  {o.id == 0 && (
                    <Chip
                      sx={{
                        ml: 1,
                        height: '20px',
                        fontSize: '0.75rem',
                      }}
                      variant="filled"
                      color="secondary"
                      label={translate('new', { ns: 'document-category' })}
                    />
                  )}
                </MenuItem>
              )}
              renderInput={params => (
                <AppTextField
                  {...params}
                  name="supplierId"
                  label={translate('supplier', { ns: 'supplier' })}
                  inputProps={{
                    ...params.inputProps,
                  }}
                  placeholder={translate('select-supplier', { ns: 'main-component' })}
                />
              )}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              PaperComponent={({ children }) => {
                return (
                  <Paper>
                    {children}
                    <Button
                      color="primary"
                      fullWidth
                      disabled={isSuppliersLoading}
                      sx={{ justifyContent: 'flex-start', pl: 2 }}
                      onMouseDown={onAddSupplier}
                      startIcon={<MdAdd />}
                      variant="contained"
                    >
                      {translate('addSupplier', { ns: 'supplier' })}
                    </Button>
                  </Paper>
                )
              }}
            />
          )}
          {errors.supplier && selectedCustomerId && (
            <Typography color="error" variant="body2" sx={{ paddingLeft: '8px', paddingBottom: '12px' }}>
              {translate('select-supplier', { ns: 'main-component' })}
            </Typography>
          )}
        </AppForm>
      </Box>
    </GenericDialogLayout>
  )
}

export default ChangeEquipmentOwnerModal
